<app-menu>
  <div class="manager-shell min-h-full bg-[var(--manager-bg)] text-[var(--manager-text)]" [attr.data-theme]="themeMode">
    <div class="mx-auto flex w-full max-w-[1840px] flex-col gap-5 px-4 py-5 sm:px-6 lg:px-8">
      <header class="manager-hero overflow-hidden rounded-[8px] border border-[var(--manager-border)] bg-[var(--manager-panel)] shadow-[0_18px_45px_var(--manager-shadow)]">
        <div class="flex flex-col gap-5 p-5 lg:flex-row lg:items-center lg:justify-between lg:p-6">
          <div class="min-w-0">
            <p class="manager-eyebrow">Admin Tools</p>
            <h1 class="manager-title">Permission Manager</h1>
            <div class="mt-4 flex flex-wrap gap-2">
              <span class="manager-pill"><i class="fa-solid fa-user-tag"></i>{{ selectedRoleLabel }}</span>
              <span class="manager-pill"><i class="fa-solid fa-bullseye"></i>{{ roleGrants.length }} Targets</span>
              <span class="manager-pill"><i class="fa-solid fa-shield-halved"></i>{{ enabledTargetCount }} Enabled</span>
              <span class="manager-pill"><i class="fa-solid fa-check-double"></i>{{ enabledActionCount }} Actions</span>
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <button type="button" class="manager-secondary-button" [routerLink]="['/menu_manager']">
              <i class="fa-solid fa-layer-group"></i>
              <span>Menu Manager</span>
            </button>
            <button type="button" class="manager-icon-button" (click)="toggleTheme()">
              <i [class]="getThemeIcon()"></i>
              <span>{{ getThemeLabel() }}</span>
            </button>
          </div>
        </div>
      </header>

      <nav class="permission-tabs" aria-label="Permission manager sections">
        <button type="button" class="permission-tab" [class.is-active]="activeTab === 'roles'" (click)="setActiveTab('roles')">
          <i class="fa-solid fa-user-shield"></i>
          <span>Role Permissions</span>
        </button>
        <button type="button" class="permission-tab" [class.is-active]="activeTab === 'targets'" (click)="setActiveTab('targets')">
          <i class="fa-solid fa-list-check"></i>
          <span>Target Registry</span>
        </button>
      </nav>

      <section *ngIf="activeTab === 'roles'" class="permission-workspace">
        <aside class="permission-role-sidebar">
          <div class="permission-sidebar-header">
            <p class="manager-eyebrow">Role Templates</p>
            <h2>Roles</h2>
          </div>
          <div class="permission-role-list">
            <button type="button" *ngFor="let role of roles" class="permission-role-button" [class.is-active]="selectedRoleKey === role.key" (click)="selectRole(role.key)">
              <span class="permission-role-button__label">{{ role.label }}</span>
              <span class="permission-role-button__key">{{ role.key }}</span>
            </button>
          </div>
        </aside>

        <section class="permission-role-detail">
          <div class="permission-detail-header">
            <div class="min-w-0">
              <p class="manager-eyebrow">Editing Permissions For</p>
              <h2 class="manager-section-title">{{ selectedRoleLabel }}</h2>
              <div class="mt-2 flex flex-wrap gap-2">
                <span class="permission-soft-pill">{{ enabledTargetCount }} of {{ roleGrants.length }} targets enabled</span>
                <span class="permission-soft-pill">{{ selectedRoleEnabledPercent }}% coverage</span>
                <span class="permission-soft-pill">{{ enabledActionCount }} actions</span>
              </div>
            </div>
            <div class="permission-header-actions">
              <mat-form-field appearance="outline" class="manager-search-field permission-copy-field">
                <mat-label>Copy From Role</mat-label>
                <mat-select [(ngModel)]="copyFromRoleKey">
                  <mat-option value="">Select role</mat-option>
                  <mat-option *ngFor="let role of copyFromRoleOptions" [value]="role.key">{{ role.label }}</mat-option>
                </mat-select>
              </mat-form-field>
              <button type="button" class="manager-secondary-button min-h-[56px]" (click)="copyTemplateFromRole()" [disabled]="!copyFromRoleKey">
                <i class="fa-solid fa-copy"></i>
                <span>Copy</span>
              </button>
              <button type="button" class="manager-primary-button min-h-[56px]" (click)="saveRoleTemplate()" [disabled]="roleSaving || targetsLoading">
                <i class="fa-solid fa-floppy-disk"></i>
                <span>{{ roleSaving ? 'Saving...' : 'Save Template' }}</span>
              </button>
            </div>
          </div>

          <div class="permission-role-filters">
            <mat-form-field appearance="outline" class="manager-search-field">
              <mat-label>Group</mat-label>
              <mat-select [(ngModel)]="selectedGroup" (selectionChange)="updateDisplayedRoleGrants()">
                <mat-option *ngFor="let group of groups" [value]="group">{{ group }}</mat-option>
              </mat-select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="manager-search-field">
              <mat-label>Search Targets</mat-label>
              <i matPrefix class="fa-solid fa-magnifying-glass"></i>
              <input matInput [(ngModel)]="filterText" (input)="updateDisplayedRoleGrants()" placeholder="drop_trailers, user details, field...">
            </mat-form-field>

            <div class="permission-filter-actions">
              <button type="button" class="permission-mini-button" (click)="applyPresetToFiltered('view')">View Visible</button>
              <button type="button" class="permission-mini-button" (click)="applyPresetToFiltered('edit')">Edit Visible</button>
              <button type="button" class="permission-mini-button" (click)="applyPresetToFiltered('full')">Full Visible</button>
              <button type="button" class="permission-mini-button" (click)="applyPresetToFiltered('none')">Clear Visible</button>
              <button type="button" class="permission-mini-button" (click)="expandAllGroups()">Expand</button>
              <button type="button" class="permission-mini-button" (click)="collapseAllGroups()">Collapse</button>
            </div>
          </div>

          <div *ngIf="roleSuccess" class="permission-message permission-message--success">{{ roleSuccess }}</div>
          <div *ngIf="roleError" class="permission-message permission-message--error">{{ roleError }}</div>
          <div *ngIf="targetsLoading" class="permission-empty-state">Loading role template...</div>

          <div class="permission-group-stack" *ngIf="!targetsLoading">
            <article class="permission-group-card" *ngFor="let section of groupedPermissionSections">
              <header class="permission-group-header">
                <button type="button" class="permission-group-toggle" (click)="toggleGroup(section.groupKey)">
                  <i class="fa-solid" [ngClass]="isGroupCollapsed(section.groupKey) ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
                  <span>{{ section.groupKey }}</span>
                </button>
                <div class="permission-group-meta">
                  <span>{{ section.enabledTargets }}/{{ section.grants.length }} enabled</span>
                  <span>{{ section.enabledActions }} actions</span>
                </div>
                <div class="permission-group-tools">
                  <button type="button" class="permission-mini-button" (click)="applyPresetToGroup(section, 'view')">View</button>
                  <button type="button" class="permission-mini-button" (click)="applyPresetToGroup(section, 'edit')">Edit</button>
                  <button type="button" class="permission-mini-button" (click)="applyPresetToGroup(section, 'full')">Full</button>
                  <button type="button" class="permission-mini-button" (click)="applyPresetToGroup(section, 'none')">Clear</button>
                </div>
              </header>

              <div class="permission-target-list" *ngIf="!isGroupCollapsed(section.groupKey)">
                <div class="permission-target-row" *ngFor="let grant of section.grants">
                  <div class="permission-target-main">
                    <div class="permission-target-titleline">
                      <span class="permission-target-title">{{ grant.label }}</span>
                      <span class="permission-type-chip">{{ grant.target_type }}</span>
                      <span class="permission-preset-chip" [ngClass]="'preset-' + getAccessPreset(grant)">{{ accessPresetLabelForRow(grant) }}</span>
                    </div>
                    <div class="permission-target-key">{{ grant.permission_key }}</div>
                    <div class="permission-target-description">{{ grant.description || '-' }}</div>
                  </div>

                  <div class="permission-crud-cluster" [attr.aria-label]="grant.label + ' access controls'">
                    <button type="button" *ngFor="let action of permissionActions" class="permission-crud-toggle" [class.is-on]="grant[action.key]" (click)="toggleGrant(grant, action.key)" [attr.aria-pressed]="grant[action.key]">
                      <span class="permission-crud-box"><i *ngIf="grant[action.key]" class="fa-solid fa-check"></i></span>
                      <span>{{ action.short }}</span>
                    </button>
                  </div>

                  <div class="permission-row-tools">
                    <button type="button" class="permission-mini-button" (click)="setRowPreset(grant, 'view')">View</button>
                    <button type="button" class="permission-mini-button" (click)="setRowPreset(grant, 'edit')">Edit</button>
                    <button type="button" class="permission-mini-button" (click)="setRowPreset(grant, 'full')">Full</button>
                    <button type="button" class="permission-mini-button" (click)="setRowPreset(grant, 'none')">Clear</button>
                  </div>
                </div>
              </div>
            </article>

            <div *ngIf="groupedPermissionSections.length === 0" class="permission-empty-state">No permission targets match this filter.</div>
          </div>
        </section>
      </section>

      <section *ngIf="activeTab === 'targets'" class="manager-panel">
        <div class="manager-section-header">
          <div>
            <p class="manager-eyebrow">Developer Registry</p>
            <h2 class="manager-section-title">Permission Targets</h2>
          </div>
          <button type="button" class="manager-primary-button" (click)="toggleTargetForm()">
            <i class="fa-solid fa-plus"></i>
            <span>{{ showTargetForm ? 'Close Target Form' : 'Add Target' }}</span>
          </button>
        </div>

        <div class="permission-registry-toolbar">
          <mat-form-field appearance="outline" class="manager-search-field">
            <mat-label>Search Registry</mat-label>
            <i matPrefix class="fa-solid fa-magnifying-glass"></i>
            <input matInput [(ngModel)]="targetFilterText" (input)="updateDisplayedTargets()" placeholder="permission key, group, route...">
          </mat-form-field>
          <span class="permission-soft-pill">{{ displayedTargetRows.length }} targets shown</span>
          <span class="permission-soft-pill">{{ targetRows.length }} total targets</span>
        </div>

        <div *ngIf="showTargetForm" class="permission-target-form rounded-lg border border-[var(--manager-border)] bg-[var(--manager-panel-soft)] p-4">
          <div class="grid gap-3 lg:grid-cols-2 xl:grid-cols-[minmax(240px,1fr)_minmax(180px,0.75fr)_minmax(220px,1fr)_160px]">
            <mat-form-field appearance="outline" class="manager-search-field">
              <mat-label>Permission Key</mat-label>
              <input matInput [(ngModel)]="targetForm.permission_key" placeholder="module.feature">
            </mat-form-field>
            <mat-form-field appearance="outline" class="manager-search-field">
              <mat-label>Group</mat-label>
              <input matInput [(ngModel)]="targetForm.group_key" placeholder="Drop Trailers">
            </mat-form-field>
            <mat-form-field appearance="outline" class="manager-search-field">
              <mat-label>Label</mat-label>
              <input matInput [(ngModel)]="targetForm.label" placeholder="Drop Note">
            </mat-form-field>
            <mat-form-field appearance="outline" class="manager-search-field">
              <mat-label>Type</mat-label>
              <mat-select [(ngModel)]="targetForm.target_type">
                <mat-option *ngFor="let type of targetTypes" [value]="type">{{ type }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
          <div class="mt-3 grid gap-3 lg:grid-cols-[minmax(260px,1fr)_160px_120px_auto] lg:items-start">
            <mat-form-field appearance="outline" class="manager-search-field">
              <mat-label>Description</mat-label>
              <input matInput [(ngModel)]="targetForm.description">
            </mat-form-field>
            <mat-form-field appearance="outline" class="manager-search-field">
              <mat-label>Route</mat-label>
              <input matInput [(ngModel)]="targetForm.route_path" placeholder="/drop_trailers">
            </mat-form-field>
            <mat-form-field appearance="outline" class="manager-search-field">
              <mat-label>Order</mat-label>
              <input matInput type="number" [(ngModel)]="targetForm.sort_order">
            </mat-form-field>
            <div class="flex min-h-[56px] items-center gap-3">
              <mat-checkbox [(ngModel)]="targetForm.active">Active</mat-checkbox>
              <button type="button" class="manager-primary-button" (click)="saveTarget()" [disabled]="targetSaving">
                <i class="fa-solid fa-check"></i>
                <span>{{ targetSaving ? 'Saving...' : 'Save Target' }}</span>
              </button>
            </div>
          </div>
        </div>

        <div *ngIf="targetSuccess" class="permission-message permission-message--success">{{ targetSuccess }}</div>
        <div *ngIf="targetError" class="permission-message permission-message--error">{{ targetError }}</div>
        <div *ngIf="targetLoading" class="permission-empty-state">Loading target registry...</div>

        <div class="manager-grid-shell permission-registry-grid-shell">
          <ag-grid-angular
            class="ag-theme-ulaap manager-grid permission-registry-grid"
            (gridReady)="onTargetGridReady($event)"
            [rowData]="displayedTargetRows"
            [columnDefs]="targetColumnDefs"
            [defaultColDef]="targetDefaultColDef"
            [rowHeight]="56"
            [headerHeight]="42">
          </ag-grid-angular>
        </div>
      </section>
    </div>
  </div>
</app-menu>
